import { useState, type ReactNode } from 'react';
import { CheckIcon, StarIcon } from 'lucide-react';

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import type { SimpleAccount } from '@/lib/types';
import { cn } from '@/lib/utils';
import { groupAccountsForPicker, splitMatch } from './accountSearch';

export interface AccountCommandProps {
  accounts: SimpleAccount[];
  /** お気に入りのアカウント ID（`SimpleAccount.id`）。 */
  favoriteAccountIds: string[];
  /** 表示中のアカウントコード。指定すると該当行に印を付ける。 */
  currentAccountCode?: string;
  onSelect: (code: string) => void;
  /** 渡したときだけ各行に☆（お気に入りの切り替え）を出す。 */
  onToggleFavorite?: (accountId: string, shouldBeFavorite: boolean) => void;
  /** Enter キーの説明（「開く」「切り替え」など）。 */
  selectLabel?: string;
  autoFocus?: boolean;
  listClassName?: string;
}

function Highlighted({ text, query }: { text: string; query: string }) {
  const { before, match, after } = splitMatch(text, query);
  return (
    <>
      {before}
      {match && <mark className="rounded-sm bg-primary/20 px-px text-foreground">{match}</mark>}
      {after}
    </>
  );
}

function Kbd({ children }: { children: ReactNode }) {
  return (
    <kbd className="inline-flex h-[18px] min-w-[18px] items-center justify-center rounded border border-b-2 bg-background px-1 font-mono text-[11px] text-muted-foreground">
      {children}
    </kbd>
  );
}

/**
 * 会社名・アカウントコードで検索して選ぶアカウントのリスト（キーボードの ↑↓ / Enter で操作できる）。
 * 絞り込みと並び順は `groupAccountsForPicker` で決めるため、cmdk 自体の絞り込みは使わない。
 */
export function AccountCommand({
  accounts,
  favoriteAccountIds,
  currentAccountCode,
  onSelect,
  onToggleFavorite,
  selectLabel = '開く',
  autoFocus = false,
  listClassName,
}: AccountCommandProps) {
  const [query, setQuery] = useState('');
  const sections = groupAccountsForPicker(accounts, query, favoriteAccountIds);
  const matchedCount = sections.reduce((count, section) => count + section.accounts.length, 0);
  const favorites = new Set(favoriteAccountIds);

  return (
    // label は検索欄の aria-labelledby になる（input の aria-label より優先される）
    <Command shouldFilter={false} label="アカウントを検索" className="rounded-none">
      <CommandInput
        placeholder="会社名・アカウントコードで検索"
        value={query}
        onValueChange={setQuery}
        autoFocus={autoFocus}
        className="h-12 text-[15px]"
      />
      <CommandList
        label="アカウント一覧"
        className={cn('h-[396px] max-h-none px-1.5 py-1', listClassName)}
      >
        <CommandEmpty>
          <p className="font-medium">「{query.trim()}」に一致するアカウントはありません</p>
          <p className="mt-1 text-muted-foreground">
            会社名の一部か、アカウントコードで検索してください
          </p>
        </CommandEmpty>
        {sections.map((section) => (
          <CommandGroup key={section.heading} heading={section.heading}>
            {section.accounts.map((account) => {
              const isCurrent = account.code === currentAccountCode;
              const isFavorite = favorites.has(account.id);
              return (
                <CommandItem
                  key={account.id}
                  value={account.code}
                  onSelect={() => onSelect(account.code)}
                  aria-current={isCurrent ? true : undefined}
                  className="h-11 gap-3 rounded-lg py-0 pl-3 pr-1 data-[selected=true]:bg-primary/10"
                >
                  {currentAccountCode !== undefined && (
                    <CheckIcon
                      aria-hidden
                      className={cn('text-primary', !isCurrent && 'invisible')}
                    />
                  )}
                  <span className={cn('min-w-0 flex-1 truncate', isCurrent && 'font-medium')}>
                    <Highlighted text={account.name} query={query} />
                  </span>
                  <span className="shrink-0 rounded-md bg-muted px-1.5 py-0.5 font-mono text-xs text-muted-foreground">
                    <Highlighted text={account.code} query={query} />
                  </span>
                  {onToggleFavorite && (
                    <button
                      type="button"
                      aria-label={`${account.name}をお気に入り${isFavorite ? 'から外す' : 'に追加'}`}
                      aria-pressed={isFavorite}
                      className={cn(
                        'inline-flex size-9 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground',
                        isFavorite && 'text-amber-500 hover:text-amber-600',
                      )}
                      // 行の選択（cmdk の onSelect）まで伝えない
                      onClick={(event) => {
                        event.stopPropagation();
                        onToggleFavorite(account.id, !isFavorite);
                      }}
                    >
                      <StarIcon aria-hidden className={cn(isFavorite && 'fill-amber-400')} />
                    </button>
                  )}
                </CommandItem>
              );
            })}
          </CommandGroup>
        ))}
      </CommandList>
      <div className="flex flex-wrap items-center gap-3 border-t bg-muted/50 px-3.5 py-2 text-xs text-muted-foreground">
        <span className="inline-flex items-center gap-1">
          <Kbd>↑</Kbd>
          <Kbd>↓</Kbd>
          移動
        </span>
        <span className="inline-flex items-center gap-1">
          <Kbd>Enter</Kbd>
          {selectLabel}
        </span>
        <span className="ml-auto">
          {query.trim()
            ? `${matchedCount} 件一致 / 全 ${accounts.length} 件`
            : `全 ${accounts.length} 件`}
        </span>
      </div>
    </Command>
  );
}
